import { useMutation } from '@apollo/client'
import { useSnackbar } from 'notistack'
import { useContext, useState } from 'react'
import { FieldErrors } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { adUnitsAttributesMapper } from 'features/inventory/domain/components/ProductAdUnitsTable/utils'
import { AddEditDomainContext } from 'features/inventory/domain/contexts/AddEditDomainContext'
import { mapGraphQLErrors } from 'providers/graphql'
import { floatPercentToDTO } from 'utils/number'
import { Schema } from '../../schema'
import { UPDATE_DOMAIN_PRICE_GENIUS } from './api/mutation'
import { Data, Variables } from './api/types'
import { UseUpdateDomainPriceGenius } from './types'

export const useUpdateDomainPriceGenius = (): UseUpdateDomainPriceGenius => {
  const { t } = useTranslation('features/domain')
  const { enqueueSnackbar } = useSnackbar()
  const { setLoading } = useContext(AddEditDomainContext)
  const [errors, setErrors] = useState<FieldErrors<Schema>>({})

  const [mutateUpdateDomainPriceGenius] = useMutation(UPDATE_DOMAIN_PRICE_GENIUS, {
    onCompleted: ({ updateDomainPriceGenius: { errors } }: Data): void => {
      setLoading(false)

      if (errors && errors.length > 0) {
        setErrors(mapGraphQLErrors(errors))
        enqueueSnackbar(t('form.error.update'), { variant: 'error' })
      } else {
        setErrors({})
        enqueueSnackbar(t('form.success.update'), { variant: 'success' })
      }
    },
    onError: (): void => setLoading(false),
  })

  const mapper = (data: Schema): Variables => {
    const { adUnitsAttributes, trafficPercent, ...result } = data

    return {
      ...result,
      adUnitsAttributes: adUnitsAttributesMapper<'priceGeniusEnabled'>(
        adUnitsAttributes,
        'priceGeniusEnabled',
      ),
      trafficPercent: floatPercentToDTO(trafficPercent),
    }
  }

  const updateDomainPriceGenius = (data: Schema) => {
    setLoading(true)

    return mutateUpdateDomainPriceGenius({
      variables: {
        input: mapper(data),
      },
    })
  }

  return { errors, updateDomainPriceGenius }
}
